<template>
  <v-container class="vpd-account-panels v-sub-content">

    <!-- Overlays and dialogs stay untouched -->
    <v-overlay :value="overlay" z-index="1001">
      <v-progress-circular indeterminate size="64" />
    </v-overlay>

    <vpd-confirm-dialog headline="Valóban le szeretné állítani a megosztást?"
      subtitle="A leállítást követően adatai nem kerülnek továbbításra a kiválasztott partnerhez. 
      Ezt azt is eredményezheti, hogy a továbbiakban nem fogja tudni igénybe venni partnerénél azt a szolgáltatást, amihez korábban a megosztást beállította." :dialog="dialog"
      event="remove-service-from-account" @remove-service-from-account="removeAccountFromService"
      @close-modal="dialog = false"></vpd-confirm-dialog>

    <vpd-confirm-dialog headline="Valóban meg szeretné szüntetni bankszámlája csatolását?"
      subtitle="Ha folytatja a megszüntetést, ezt követően bankszámla és tranzakciós adatai nem fognak frissülni a bankszámlakivonat.hu rendszerében. Ez hatással lehet az Ön által korábban beállított számlatörténet megosztásokra is. Biztosan folytatja a megszüntetést?"
      :dialog="consentCancelDialog" event="remove-bank-account-from-account"
      @remove-bank-account-from-account="removeBankAccountFromAccount"
      @close-modal="consentCancelDialog = false"></vpd-confirm-dialog>

    <vpd-confirm-dialog headline="Valóban meg szeretné újítani a megosztást?"
      subtitle="A megosztás megújításának első lépéseként bankja az alábbi adatok megadását kéri. Ezt követően majd átirányítjuk a bank oldalára, ahol a bank elvégzi a további beazonosítást."
      :dialog="consentRenewalDialog" :bankId="selectedBank" :schema="schema" event="renew-consent"
      @renew-consent="renewConsent" @close-modal="consentRenewalDialog = false"></vpd-confirm-dialog>

    <!-- MAIN LIST (NOW PAGINATED) -->
    <section v-for="group in pagedAccountGroups" :key="group.key" class="vpd-account-group">
      <h2 class="vpd-account-group__title subtitle-1 font-weight-bold">
        {{ group.label }}
      </h2>

      <v-expansion-panels accordion>
        <v-expansion-panel v-for="account in group.accounts" :key="account.id" class="vpd-account-panel">

          <!-- PANEL HEADER -->
          <v-expansion-panel-header class="vpd-expand-header pa-0">
            <v-row align="center" class="vpd-account-panel-row">
              <v-col cols="12" lg="6" class="d-flex align-center">

                <img :src="getBankMiniImgUrl(account.bank)" />

                <div class="ps-3" :class="{
                  'green-glow': !isStructuredStatementAccount(account) && account.isThereAnyConsent,
                  'red-glow': !isStructuredStatementAccount(account) && !account.isThereAnyConsent
                }">
                  <div v-if="account.name" class="subtitle-2 font-weight-bold">
                    {{ account.name }}
                  </div>

                  <div v-if="isStructuredStatementAccount(account)" class="subtitle-2 font-weight-bold">
                    {{ account.bban || account.statementNumber || "-" }} - {{ account.currency }}
                  </div>

                  <div v-else-if="!account.isThereAnyConsent" class="caption">
                    A banki szinkronizáció leállt. Kérjük csatolja újra a bankszámláját!
                  </div>

                  <div v-else class="caption">
                    Jelenleg napi 4 alkalommal frissítjük az adatait,
                    várhatóan a következő időpontig:
                    <span class="font-weight-bold">
                      {{ onlyDate(account.expireAt) }}
                    </span>
                  </div>
                </div>
              </v-col>

              <!-- RIGHT SIDE -->
              <v-col cols="12" lg="3" class="d-flex align-center"
                v-if="account.connectedServices[0] || !$vuetify.breakpoint.xs">

                <div v-if="account.connectedServices[0]" class="pl-15 pl-lg-0">
                  <a href="#">{{ account.connectedServices.length }} megosztás beállítva</a>
                </div>

                <div v-if="account.connectedServices[0]" class="pl-15 pl-lg-0">
                  <a href="#">{{ account.sharedWith }}</a>
                </div>

              </v-col>

              <v-col cols="12" lg="3" class="d-flex align-center text-lg-right pr-lg-8">
                <v-spacer class="d-none d-lg-flex" />
                <span v-if="!isStructuredStatementAccount(account) && account.balance && !account.deletedAt"
                  class="subtitle-1 font-weight-bold">
                  {{ getNiceValue(account.balance, account.currency) }}
                </span>
                <span v-else-if="!isStructuredStatementAccount(account)" class="subtitle-1 font-weight-bold">
                  -
                </span>
              </v-col>
            </v-row>

            <template v-slot:actions>
              <vpd-chevron-down></vpd-chevron-down>
            </template>
          </v-expansion-panel-header>

          <!-- PANEL CONTENT -->
          <v-expansion-panel-content>
            <v-container class="vpd-account-panel-placeholder">
              <v-row v-if="!isStructuredStatementAccount(account)">

                <v-col cols="12" lg="6">
                  <span class="subtitle-2 font-weight-bold">Számlaszám:</span>
                  <span v-if="account.bban" class="subtitle-2 vpd-account-panel-span">
                    {{ account.bban }} - {{ account.currency }}
                  </span>
                </v-col>

                <v-col cols="12" lg="6">
                  <div>
                    <span class="d-flex align-center">
                      <a href="#" v-if="account.isThereAnyConsent" @click.stop="openConsentAttachDialog(account, true)">
                        Megújítás
                      </a>
                      <a href="#" v-else @click.stop="openConsentAttachDialog(account, false)">
                        Csatolás
                      </a>
                    </span>
                  </div>

                  <div>
                    <span class="d-flex align-center" v-if="account.isThereAnyConsent">
                      <a href="#" @click.stop="openConsentCancelDialog(account)">
                        Megszüntetés
                      </a>
                    </span>
                  </div>
                </v-col>

              </v-row>

              <!-- STRUCTURED STATEMENT DETAILS -->
              <v-row v-if="isStructuredStatementAccount(account)">
                <v-col class="vpd-account-panel__statement-details subtitle-2">
                  <div class="vpd-account-panel__statement-detail">
                    <span class="font-weight-bold">Utolsó feltöltött kivonat sorszáma:</span>
                    <span>{{ account.statementNumber || "-" }}</span>
                  </div>
                  <div class="vpd-account-panel__statement-detail">
                    <span class="font-weight-bold">Utolsó feltöltött kivonat időszaka:</span>
                    <span>{{ getStatementPeriod(account) }}</span>
                  </div>
                  <div class="vpd-account-panel__statement-detail">
                    <span class="font-weight-bold">Utolsó feltöltött kivonat záróegyenlege:</span>
                    <span>{{ getNiceValue(account.balance, account.currency) }}</span>
                  </div>
                </v-col>
              </v-row>

              <!-- CONNECTED SERVICES -->
              <v-row>
                <v-col>
                  <span class="subtitle-1 font-weight-bold">
                    Ehhez a bankszámlához jelenleg az alábbi megosztások tartoznak:
                  </span>

                  <div v-if="!account.connectedServices[0]" class="vpd-account-panel__account-services-empty">
                    Még nem osztotta meg senkivel a számlatörténetét.
                  </div>

                  <v-row v-for="(service, sIndex) in account.connectedServices" :key="service.id"
                    class="vpd-account-panel__account-services-row">
                    <v-col class="d-flex align-center">
                      <img class="small" :src="getServiceImgUrl(service)" />
                      <span class="body-1 ps-3">{{ service.name }}</span>
                    </v-col>

                    <v-col class="d-flex align-center vpd-account-panel-remove">
                      <a href="#" @click.stop="
                        openDialogForSelectedRow(
                          service.id,
                          account.id,
                          sIndex,
                          getAccountIndex(account)
                        )
                        ">
                        Megosztás eltávolítása
                      </a>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>

            </v-container>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </section>

    <!-- PAGINATION -->
    <div class="d-flex justify-center my-8">
      <v-pagination v-model="page" :length="pageCount" total-visible="7" circle />
    </div>

    <v-divider />
  </v-container>
</template>

<script>
import "./_vpd-account-panel.scss";
import moment from "moment";
import {
  getFormattedAccountNumber,
  getFormattedAmountValue,
  getBankImgUrl,
  getBankMiniImgUrl,
  getServiceImgUrl,
} from "@/modules/helpers";
import VpdConfirmDialog from "@/components/vpd-confirm-dialog/vpd-confirm-dialog";
import VpdChevronDown from "@/components/icons/VpdChevronDown";
import servicesService from "@/services/services-service";
import bankAccountConsentService from "@/services/bank-account-consent-service";
import bankRequirementsService from "@/services/bank-requirements-service";
import bankListService from "@/services/banklist-service";

const STRUCTURED_STATEMENT_STATUS = "Imported from structured statement";
const ACCOUNT_GROUPS = [
  {
    key: "connected",
    label: "Csatolt bankszámlák",
    isMatch: account => account.lastSyncStatus !== STRUCTURED_STATEMENT_STATUS
  },
  {
    key: "structured-statement",
    label: "PDF kivonatról létrehozott bankszámlák",
    isMatch: account => account.lastSyncStatus === STRUCTURED_STATEMENT_STATUS
  }
];

export default {
  name: "vpd-account-panel",
  props: {
    accounts: { type: Array, required: true },
    config: Object,
    itemsPerPage: { type: Number, default: 5 }
  },
  components: { VpdChevronDown, VpdConfirmDialog },

  data() {
    return {
      // pagination
      page: 1,

      // dialogs
      dialog: false,
      consentCancelDialog: false,
      consentRenewalDialog: false,
      isRenewal: false,

      // state
      overlay: false,
      redirectUrl: null,
      accountId: null,
      selectedBank: null,
      schema: null
    };
  },

  computed: {
    orderedAccounts() {
      return ACCOUNT_GROUPS.reduce((accounts, group) => {
        return accounts.concat(this.accounts.filter(group.isMatch));
      }, []);
    },
    pagedAccountGroups() {
      const start = (this.page - 1) * this.itemsPerPage;
      const pagedAccounts = this.orderedAccounts.slice(
        start,
        start + this.itemsPerPage
      );

      return ACCOUNT_GROUPS.map(group => ({
        key: group.key,
        label: group.label,
        accounts: pagedAccounts.filter(group.isMatch)
      })).filter(group => group.accounts.length > 0);
    },
    pageCount() {
      return Math.ceil(this.orderedAccounts.length / this.itemsPerPage) || 1;
    }
  },

  watch: {
    accounts() {
      this.page = 1;
    }
  },

  methods: {
    isStructuredStatementAccount(account) {
      return account.lastSyncStatus === STRUCTURED_STATEMENT_STATUS;
    },
    getStatementPeriod(account) {
      const start = this.formatStatementDate(account.latestStatementPeriodStart);
      const end = this.formatStatementDate(account.latestStatementPeriodEnd);

      if (start && end) {
        return `${start} - ${end}`;
      }
      if (start) {
        return `${start} -`;
      }
      if (end) {
        return `- ${end}`;
      }

      return "-";
    },
    formatStatementDate(date) {
      return date ? moment(date, "YYYY-MM-DD").format("YYYY.MM.DD.") : null;
    },
    getAccountIndex(account) {
      return this.accounts.indexOf(account);
    },

    /* original methods preserved… */
    diffInDays(date) { return moment().diff(date, "days"); },
    since(date) { moment.locale("hu"); return moment(Date.parse(date)).fromNow(); },
    onlyDate(date) { moment.locale("hu"); return moment(date).format("YYYY-MM-DD"); },
    getBankImgUrl, getBankMiniImgUrl, getServiceImgUrl,
    getFormattedAccountNumber, getNiceValue: getFormattedAmountValue,

    /* the rest of your original methods remain exactly unchanged */
    openDialogForSelectedRow(serviceId, accountId, serviceIndex, accountIndex) {
      this.dialog = true;
      this.selectedServiceId = serviceId;
      this.selectedAccountId = accountId;
      this.selectedServiceIndex = serviceIndex;
      this.selectedAccountIndex = accountIndex;
    },

    async openConsentAttachDialog(account, isUnderRenewal) {
      this.isRenewal = isUnderRenewal;
      this.selectedBank = account.bank;
      this.accountId = account.id;
      this.overlay = true;
      this.redirectUrl = this.config.aspspProviders.find(p => p.id == this.selectedBank).auth.redirect;

      const resp = await bankRequirementsService.get(this.selectedBank);
      this.schema = resp.items;
      this.overlay = false;
      this.consentRenewalDialog = true;
    },

    async removeAccountFromService() {
      this.dialog = false;

      await servicesService.removeAccountFromService(
        this.selectedServiceId,
        this.selectedAccountId
      );

      this.accounts[this.selectedAccountIndex].connectedServices.splice(
        this.selectedServiceIndex,
        1
      );

      this.selectedServiceIndex = null;
      this.selectedAccountIndex = null;

      this.$emit("refreshUserData");
    },

    openConsentCancelDialog(account) {
      this.accountId = account.id;
      this.consentCancelDialog = true;
    },

    async removeBankAccountFromAccount() {
      let resp;
      do {
        resp = await bankAccountConsentService.delete(this.accountId);
      } while (resp === undefined);

      this.$emit("refreshUserData");
      this.consentCancelDialog = false;
    },

    async renewConsent(formValues) {
      const body = Object.fromEntries(
        Object.entries(formValues).map(([k, v]) => [k.toLowerCase(), v])
      );

      this.consentRenewalDialog = false;
      this.overlay = true;

      let attempts = 3;
      let success = false;

      while (!success && attempts-- > 0) {

        if (this.isRenewal) {
          const path = await bankAccountConsentService.renew(this.accountId, body);
          if (path) {
            window.location = path;
            success = true;
          }
        } else {
          const resp = await bankListService.select(this.redirectUrl, body);
          if (resp) {
            window.location = resp.url;
            success = true;
          }
        }
      }

      this.overlay = false;
    }
  }
};
</script>
